import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

// ── ImportFinale ──────────────────────────────────────────────────────────────
// The arrival moment. When a migrating user (TV Time, Trakt, …) finishes an
// import, their years of viewing history land in NextEp all at once — the single
// most emotional beat of the whole onboarding. It used to resolve to a tiny corner
// toast ("247 shows archived 📦"); this is the payoff it deserves: a projector
// spins up, their posters cascade into a wall, and a ticker rolls the count up to
// "Your archive is restored."
//
// Fires on window event `nextep:import-complete` with detail:
//   { added:Number, posters:[path…], years:[Number…] }
// Coordinates with the celebration gate (raises __nextepFinaleActive while up) so a
// queued Marquee/toast can't land on it.

function ReelHub({ spinning }) {
  return (
    <svg width={70} height={70} viewBox="0 0 100 100" style={{
      filter: 'drop-shadow(0 6px 20px color-mix(in srgb, var(--amber) 45%, transparent))',
      '--nxa': spinning ? 'ifReelSpin 2.6s linear infinite' : 'none',
    }}>
      <circle cx="50" cy="50" r="46" fill="none" stroke="var(--amber)" strokeWidth="5" opacity="0.9"/>
      <circle cx="50" cy="50" r="12" fill="var(--amber)"/>
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2
        return <circle key={i} cx={50 + Math.cos(a) * 30} cy={50 + Math.sin(a) * 30} r="7.5"
          fill="none" stroke="var(--amber)" strokeWidth="4" opacity="0.85"/>
      })}
    </svg>
  )
}

export function ImportFinale() {
  const navigate = useNavigate()
  const [data, setData]   = useState(null)   // { added, posters, years }
  const [phase, setPhase] = useState(0)       // 0=hidden 1=reel 2=wall 3=exit
  const [count, setCount] = useState(0)
  const timers = useRef([])
  // Mirror of `data` for walk() — the auto-dismiss timer is created once at mount, so its
  // closure can't see the latest `data` state; the ref always holds the current values.
  const dataRef = useRef(null)
  const clear  = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  const later  = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t) }

  useEffect(() => {
    if (!document.getElementById('import-finale-styles')) {
      const el = document.createElement('style')
      el.id = 'import-finale-styles'
      el.textContent = STYLES
      document.head.appendChild(el)
    }
    const handler = (e) => {
      const d = e.detail || {}
      const added = Number(d.added) || 0
      if (added < 1) return
      clear()
      if (typeof window !== 'undefined') window.__nextepFinaleActive = true
      // "watched" imports mint cards → the binder; "plan" imports fill the watchlist →
      // the list. The copy + CTA branch on mode so a Plan-to-Watch import never claims
      // "minted to your binder" and then dumps the user in an empty /cards binder.
      const payload = { added, mode: d.mode === 'plan' ? 'plan' : 'watched', unmatched: Number(d.unmatched) || 0, posters: (d.posters || []).filter(Boolean).slice(0, 48), years: (d.years || []).filter(Boolean) }
      dataRef.current = payload
      setData(payload)
      setCount(0)
      setPhase(1)
      later(() => setPhase(2), 900)            // reel spins, then the wall assembles
      // Count roll — eased toward the total over ~1.6s starting with the wall.
      const total = added
      const start = 1000
      const durMs = 1600
      const steps = Math.min(total, 60)
      for (let s = 1; s <= steps; s++) {
        later(() => setCount(Math.round((s / steps) * total)), start + (durMs * s) / steps)
      }
      later(() => setPhase(3), 6400)
      // Auto-dismiss must NAVIGATE (via walk), not just reset — otherwise a passive
      // user is silently dropped back onto the raw import sheet instead of their list.
      later(() => walk(), 6900)
    }
    window.addEventListener('nextep:import-complete', handler)
    return () => {
      window.removeEventListener('nextep:import-complete', handler)
      clear()
      // If we unmount mid-cinematic, release the gate — a stuck __nextepFinaleActive
      // would silently mute every future celebration until reload.
      if (typeof window !== 'undefined') window.__nextepFinaleActive = false
    }
  }, [])

  const reset = () => {
    clear()
    if (typeof window !== 'undefined') window.__nextepFinaleActive = false
    dataRef.current = null
    setPhase(0)
    setTimeout(() => setData(null), 450)
  }

  const isPlan = data?.mode === 'plan'
  const unmatched = data?.unmatched || 0
  // Where the finale hands off. If some titles didn't match, DON'T navigate away — the
  // import modal (with its UnmatchedRecovery picker + follow/invite surfaces) is still
  // open behind us, so we just dismiss the cinematic to reveal it. When everything
  // matched, land on a filter that actually has content: a watched import fills the
  // 'completed' bucket (My List defaults to the empty 'watching' tab), and a plan import
  // fills 'plan_to_watch' — a bare /list would read as empty either way.
  const walk = () => {
    const d = dataRef.current
    reset()
    if ((d?.unmatched || 0) > 0) return   // stay for recovery
    navigate(d?.mode === 'plan' ? '/list?filter=plan_to_watch' : '/list?filter=all')
  }

  const yearSpan = useMemo(() => {
    if (!data?.years?.length) return null
    const lo = Math.min(...data.years), hi = Math.max(...data.years)
    if (!lo || lo === hi) return null
    return `${lo}–${hi}`
  }, [data])

  if (!data) return null
  const posters = data.posters

  return (
    <div onClick={phase >= 2 ? walk : undefined} style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)',
      background: 'radial-gradient(ellipse at 50% 42%, var(--bg-raised) 0%, var(--bg) 68%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font)', overflow: 'hidden', userSelect: 'none',
      opacity: phase >= 1 && phase < 3 ? 1 : 0, transition: 'opacity 0.45s ease',
    }}>

      {/* Poster wall, cascades in behind the number */}
      <div aria-hidden style={{
        position: 'absolute', inset: '-4% -2%', display: 'flex', flexWrap: 'wrap',
        gap: 6, alignContent: 'center', justifyContent: 'center',
        opacity: phase >= 2 ? 0.26 : 0, transition: 'opacity 0.8s ease',
        maskImage: 'radial-gradient(ellipse at 50% 45%, transparent 24%, #000 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at 50% 45%, transparent 24%, #000 70%)',
        pointerEvents: 'none',
      }}>
        {posters.map((p, i) => (
          <div key={i} style={{
            width: 62, height: 93, borderRadius: 5, overflow: 'hidden',
            background: 'var(--bg-raised)', flexShrink: 0,
            '--d': `${Math.min(i * 28, 1200)}ms`,
          }} className={phase >= 2 ? 'nxa-if-poster' : undefined}>
            <img src={`https://image.tmdb.org/t/p/w200${p}`} alt="" loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ))}
      </div>

      {/* Projector reel */}
      <div style={{ marginBottom: 22, zIndex: 1 }} className={phase >= 1 ? 'nxa-if-rise' : undefined}>
        <ReelHub spinning={phase >= 1 && phase < 3} />
      </div>

      {/* The count */}
      <div style={{
        color: 'var(--text-strong)', fontWeight: 800, fontSize: 'clamp(56px, 20vw, 108px)', lineHeight: 0.9,
        letterSpacing: '-0.03em', zIndex: 1,
        textShadow: '0 4px 40px color-mix(in srgb, var(--amber) 55%, transparent)',
        opacity: phase >= 2 ? 1 : 0,
      }} className={phase >= 2 ? 'nxa-if-rise' : undefined}>
        {count.toLocaleString()}
      </div>
      <div style={{
        color: 'var(--amber)', fontWeight: 700, fontSize: 13, letterSpacing: '0.22em',
        textTransform: 'uppercase', marginTop: 6, marginBottom: 20, zIndex: 1,
        opacity: phase >= 2 ? 1 : 0, transition: 'opacity 0.5s ease 0.3s',
      }}>
        {isPlan
          ? (data.added === 1 ? 'show queued' : 'shows queued')
          : (data.added === 1 ? 'show restored' : 'shows restored')}
      </div>

      {/* Headline */}
      <div style={{
        color: 'var(--text-strong)', fontWeight: 800, fontSize: 'clamp(22px, 6.5vw, 30px)', letterSpacing: '-0.02em',
        textAlign: 'center', padding: '0 28px', zIndex: 1,
        '--d': '0.45s', opacity: phase >= 2 ? undefined : 0,
      }} className={phase >= 2 ? 'nxa-if-fadeup' : undefined}>
        {isPlan ? 'Your watchlist is loaded.' : 'Your archive is restored.'}
      </div>
      <div style={{
        color: 'var(--text-dim)', fontSize: 13.5, marginTop: 10, textAlign: 'center',
        padding: '0 28px', zIndex: 1,
        '--d': '0.6s', opacity: phase >= 2 ? undefined : 0,
      }} className={phase >= 2 ? 'nxa-if-fadeup' : undefined}>
        {isPlan
          ? `Everything you're planning to watch, ready when you are${yearSpan ? ` · ${yearSpan}` : ''}.`
          : unmatched > 0
            ? `${data.added.toLocaleString()} restored · ${unmatched} still need a quick match.`
            : `Every show you tracked, right where you left off${yearSpan ? ` · ${yearSpan}` : ''}.`}
      </div>

      {/* CTA */}
      <button onClick={(e) => { e.stopPropagation(); walk() }} style={{
        marginTop: 28, zIndex: 1,
        background: 'var(--grad-amber)', color: 'var(--amber-ink)',
        border: 'none', borderRadius: 'var(--radius-sm)', padding: '13px 26px',
        fontWeight: 800, fontSize: 15, fontFamily: 'var(--font)', cursor: 'pointer',
        boxShadow: '0 8px 30px color-mix(in srgb, var(--amber) 40%, transparent)',
        '--d': '0.8s', opacity: phase >= 2 ? undefined : 0,
      }} className={phase >= 2 ? 'nxa-if-fadeup' : undefined}>
        {unmatched > 0 ? 'Match the rest →' : 'See your list →'}
      </button>

      <div style={{
        position: 'absolute', bottom: 'max(30px, env(safe-area-inset-bottom))', color: 'var(--text-dim)', fontSize: 11, zIndex: 1,
        opacity: phase >= 2 ? 1 : 0, transition: 'opacity 0.4s ease 1s',
      }}>
        Tap anywhere to continue
      </div>
    </div>
  )
}

const STYLES = `
@keyframes ifReelSpin { to { transform: rotate(360deg); } }
@keyframes ifRise {
  from { transform: translateY(18px) scale(0.9); opacity: 0; }
  to   { transform: translateY(0)    scale(1);   opacity: 1; }
}
@keyframes ifFadeUp {
  from { transform: translateY(14px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes ifPosterIn {
  from { transform: translateY(22px) scale(0.6); opacity: 0; }
  to   { transform: translateY(0)    scale(1);   opacity: 1; }
}
`
